import React, { useEffect, useRef, useState } from 'react';
import './Collapsable.less';
import classNames from 'classnames';
import Icon from '../Icon/Icon';
import AnimateHeight from 'react-animate-height';

type Props = {
    triggerText: string | React.ReactNode;
    arrowPosition?: 'left' | 'right' | 'none';
    children: React.ReactNode;
    className?: string;
    onChange?: (open: boolean) => void;
    open?: boolean; // Controlled mode
    defaultOpen?: boolean; // Uncontrolled mode initial state
    showChildrenDirect?: boolean;
    hideTriggerTextIfOpen?: boolean;
    /**
     * Skip rendering children until the section has been opened at least once.
     * AnimateHeight otherwise keeps them mounted at height 0, which is fine for
     * a handful of nodes but not for a list of thousands. Opt-in, so existing
     * usages keep mounting their children eagerly.
     */
    lazyChildren?: boolean;
};

const Collapsable = ({
    triggerText,
    children,
    className,
    onChange,
    open,
    defaultOpen = false,
    arrowPosition = 'left',
    showChildrenDirect = false,
    hideTriggerTextIfOpen = false,
    lazyChildren = false,
}: Props) => {
    const isControlled = open != null;
    const [internalOpen, setInternalOpen] = useState(defaultOpen);
    const isOpen = isControlled ? !!open : internalOpen;
    const [scrollOnOpen, setScrollOnOpen] = useState(false);
    // Once opened, keep children mounted so collapsing stays instant.
    const [hasBeenOpened, setHasBeenOpened] = useState(isOpen);

    const contentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (isOpen) setHasBeenOpened(true);
    }, [isOpen]);

    const toggleOpen = () => {
        const next = !isOpen;

        if (next) {
            // Set together with the open state so the children are present in the
            // same render that starts the height animation.
            setHasBeenOpened(true);
        }

        // ONLY allow scroll when user clicks trigger AND component is uncontrolled
        if (!isControlled) {
            setScrollOnOpen(next);
            setInternalOpen(next);
            onChange?.(next);
        } else {
            // controlled: Collapsable itself should not cause scroll at all, handle it manually if needed
            onChange?.(next);
        }
    };

    const renderedChildren = lazyChildren && !hasBeenOpened ? null : children;

    if (showChildrenDirect) {
        return <>{children}</>;
    }

    return (
        <div className="collapsable w-full">
            {!(isOpen && hideTriggerTextIfOpen) && (
                <div
                    className={classNames(
                        'collapsable-trigger cursor-pointer flex items-center',
                        {
                            'justify-between flex-row-reverse': arrowPosition === 'right',
                            '-ml-1': arrowPosition !== 'right',
                        },
                        className,
                    )}
                    onClick={toggleOpen}
                    onKeyDown={(event) => {
                        if (event.key !== 'Enter' && event.key !== ' ') {
                            return;
                        }

                        toggleOpen();
                    }}
                >
                    {arrowPosition !== 'none' && (
                        <Icon
                            aria={{ 'aria-hidden': true }}
                            path={Icon.Path.mdiChevronRight}
                            className={classNames('w-5 transform transition flex-shrink-0', {
                                'rotate-90': isOpen,
                            })}
                        />
                    )}
                    <div className="flex-grow">{triggerText}</div>
                </div>
            )}

            <AnimateHeight
                duration={150}
                height={!isOpen ? 0 : 'auto'}
                animateOpacity={true}
                onHeightAnimationEnd={() => {
                    if (!scrollOnOpen) {
                        return;
                    }

                    if (isOpen && contentRef.current) {
                        contentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }

                    setScrollOnOpen(false);
                }}
            >
                <div
                    ref={contentRef}
                    className={classNames('collapsable-content origin-top transition-all', { 'collapsable-content-open': isOpen })}
                    style={{ height: !isOpen ? 0 : 'auto' }}
                >
                    {renderedChildren}
                </div>
            </AnimateHeight>
        </div>
    );
};

export default Collapsable;
